'use client';

/**
 * Запросы к API в виде хуков.
 *
 * Тонкая обёртка над endpoints: собирает ключ кэша и настройки в одном месте,
 * чтобы экраны не расходились в том, что и как долго живёт.
 */

import { useQueries, useQuery } from '@tanstack/react-query';
import { queryKeys } from './keys';
import {
  getActivityHeatmap,
  getAnalysisInput,
  getCtHandlingReport,
  getUnitAddendum,
  listAdNames,
  listCallGoals,
  listUnitAddenda,
  getAnalysisSettings,
  getCall,
  getContact,
  getConversion,
  getCtSitesReport,
  getCtSourcesReport,
  getDashboardMetrics,
  getEmployeeRules,
  getHandoff,
  getIntegration,
  getIntegrationDictionaries,
  getIntegrationMappings,
  getNotificationSettings,
  getRating,
  getScoreDynamics,
  getSummary,
  getTopViolations,
  getUnitScorecards,
  getViolations,
  listAdminTenants,
  listAssignments,
  listBindings,
  listAvailableNumbers,
  listCallFields,
  listCallFieldTemplates,
  listCalls,
  listComments,
  listCtNumbers,
  listCtSessions,
  listCtSites,
  listDeliveries,
  listDestinations,
  listDisclosureGrants,
  listEmployees,
  listExtensions,
  listIntegrationKinds,
  listIntegrations,
  listGateways,
  listMissedActions,
  listMissedCalls,
  listNotificationRules,
  listNotifications,
  listOutboundDeliveries,
  listPendingOrganizations,
  listPools,
  listQueues,
  listRules,
  listScorecards,
  listScorecardTemplates,
  listServers,
  listStaticRules,
  listSources,
  listStorageProfiles,
  listTagRules,
  listTags,
  listTagTemplates,
  listTenantAdmins,
  listTenantUnits,
  listUnits,
  listUsers,
  listVocabulary,
} from './endpoints';
import type {
  CallFilters,
  CallGoalStatus,
  ConversionFilters,
  CtSessionFilters,
  CtSourcesFilters,
  DeliveryFilters,
  DirectoryFilters,
  DisclosureFilters,
  EmployeeFilters,
  MissedFilters,
  ReportFilters,
  Unit,
} from './types';

/**
 * Список звонков.
 *
 * `placeholderData` держит прошлую страницу на экране, пока грузится
 * следующая: без него таблица схлопывается в пустоту на каждый чих фильтра,
 * и страница дёргается.
 */
/**
 * Список звонков.
 *
 * `live` включает подтягивание новых записей раз в полминуты. Смысл имеет
 * только на первой странице без фильтров: там свежий звонок и должен
 * появиться сверху. На второй странице или под фильтром обновление
 * переставляло бы строки под курсором, пока человек их читает, — поэтому
 * решение принимает экран, а не хук. В фоновой вкладке TanStack Query
 * опрос не ведёт, лишних запросов от открытого весь день списка не будет.
 */
export function useCalls(filters: CallFilters, live = false, enabled = true) {
  return useQuery({
    queryKey: queryKeys.calls(filters),
    queryFn: ({ signal }) => listCalls(filters, signal),
    placeholderData: (previous) => previous,
    refetchInterval: live ? 30_000 : false,
    // Поиск в шапке молчит, пока набрано мало: запрос без фильтра вернул бы
    // просто последние звонки, и они мелькали бы вместо подсказки.
    enabled,
  });
}

export function useCall(id: string | null) {
  return useQuery({
    queryKey: queryKeys.call(id ?? ''),
    queryFn: () => getCall(id!),
    enabled: Boolean(id),
  });
}

/** Дерево подразделений. Меняется редко — держим дольше обычного. */
export function useUnits() {
  return useQuery({
    queryKey: queryKeys.units,
    queryFn: listUnits,
    staleTime: 5 * 60_000,
  });
}

/**
 * Источники. `tenant` читается только у платформы и задаёт, в чьём тенанте
 * смотрим: без него сотрудник платформы видит свои, а не клиентские.
 *
 * Тенант входит в ключ кэша: иначе список одного клиента показался бы под
 * видом списка другого при переключении в мастере.
 */
export function useSources(tenant?: string) {
  return useQuery({
    queryKey: [...queryKeys.sources, tenant ?? ''],
    queryFn: () => listSources(tenant),
    staleTime: 5 * 60_000,
  });
}

export function useEmployees(filters: EmployeeFilters = {}) {
  return useQuery({
    queryKey: queryKeys.employees(filters),
    queryFn: () => listEmployees(filters),
    staleTime: 60_000,
  });
}

export interface FlatUnit {
  id: string;
  name: string;
  /** Глубина в дереве — для отступа в выпадающем списке. */
  depth: number;
  status: Unit['status'];
}

/**
 * Дерево в плоский список для `<select>`.
 *
 * Порядок обхода — в глубину, так что дети идут сразу за родителем и отступ
 * читается как структура.
 */
export function flattenUnits(units: Unit[] | undefined, depth = 0): FlatUnit[] {
  if (!units) return [];
  return units.flatMap((unit) => [
    { id: unit.id, name: unit.name, depth, status: unit.status },
    ...flattenUnits(unit.children, depth + 1),
  ]);
}

/** Маршруты источника. Запрашиваются, только когда строку раскрыли. */
export function useBindings(sourceId: string | null, tenant?: string) {
  return useQuery({
    queryKey: [...queryKeys.bindings(sourceId ?? ''), tenant ?? ''],
    queryFn: () => listBindings(sourceId!, tenant),
    enabled: Boolean(sourceId),
  });
}

/** Дерево клиента — для выбора узла маршрута: узел принадлежит ему, а не нам. */
export function useTenantUnits(tenantId: string | null) {
  return useQuery({
    queryKey: ['admin', 'tenants', tenantId ?? '', 'units'],
    queryFn: () => listTenantUnits(tenantId!),
    enabled: Boolean(tenantId),
  });
}

export function useScorecards() {
  return useQuery({
    queryKey: queryKeys.scorecards,
    queryFn: listScorecards,
    staleTime: 5 * 60_000,
  });
}

export function useRules(scorecardId: string | null) {
  return useQuery({
    queryKey: queryKeys.rules(scorecardId ?? ''),
    queryFn: () => listRules(scorecardId!),
    enabled: Boolean(scorecardId),
  });
}

export function useAssignments(employeeId: string | null) {
  return useQuery({
    queryKey: queryKeys.assignments(employeeId ?? ''),
    queryFn: () => listAssignments(employeeId!),
    enabled: Boolean(employeeId),
  });
}

/**
 * Все маршруты всех источников одним списком.
 *
 * Привязка добавочного заводится на маршрут, а маршруты живут внутри
 * источников. Спрашивать у администратора «сначала выберите источник»
 * незачем: домен он знает, а к какому источнику тот относится — нет.
 */
export function useAllBindings() {
  const sources = useSources();
  const sourceIds = (sources.data ?? []).map((source) => source.id);

  const results = useQueries({
    queries: sourceIds.map((id) => ({
      queryKey: queryKeys.bindings(id),
      queryFn: () => listBindings(id),
      staleTime: 60_000,
    })),
  });

  return {
    isPending: sources.isPending || results.some((result) => result.isPending),
    /**
     * Источник приписывается к каждой привязке: сама привязка его не несёт,
     * а он нужен, чтобы спросить справочник добавочных — тот живёт внутри
     * источника.
     */
    data: results.flatMap((result, index) =>
      (result.data ?? []).map((binding) => ({ ...binding, sourceId: sourceIds[index] }))
    ),
  };
}

/** Пользователи тенанта. Права меняются редко, но кэшировать их надолго нельзя. */
export function useUsers() {
  return useQuery({
    queryKey: queryKeys.users,
    queryFn: listUsers,
    staleTime: 0,
  });
}

export function useDisclosureGrants(filters: DisclosureFilters = {}) {
  return useQuery({
    queryKey: queryKeys.disclosureGrants(filters),
    queryFn: () => listDisclosureGrants(filters),
  });
}

/**
 * Станции платформы.
 *
 * Запрашиваются только у платформенных сотрудников: остальным ручка отвечает
 * 404, и гонять этот запрос ради заведомого отказа незачем.
 */
export function useServers(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.servers,
    queryFn: listServers,
    enabled,
    staleTime: 60_000,
  });
}

// ─────────────────────── Справочники со станции ─────────────────────────

export function useExtensions(sourceId: string | null, filters: DirectoryFilters = {}) {
  return useQuery({
    queryKey: queryKeys.directory(sourceId ?? '', 'extensions', filters),
    queryFn: () => listExtensions(sourceId!, filters),
    enabled: Boolean(sourceId),
  });
}

export function useDestinations(sourceId: string | null, filters: DirectoryFilters = {}) {
  return useQuery({
    queryKey: queryKeys.directory(sourceId ?? '', 'destinations', filters),
    queryFn: () => listDestinations(sourceId!, filters),
    enabled: Boolean(sourceId),
  });
}

export function useQueues(sourceId: string | null, filters: DirectoryFilters = {}) {
  return useQuery({
    queryKey: queryKeys.directory(sourceId ?? '', 'queues', filters),
    queryFn: () => listQueues(sourceId!, filters),
    enabled: Boolean(sourceId),
  });
}

export function useGateways(sourceId: string | null, filters: DirectoryFilters = {}) {
  return useQuery({
    queryKey: queryKeys.directory(sourceId ?? '', 'gateways', filters),
    queryFn: () => listGateways(sourceId!, filters),
    enabled: Boolean(sourceId),
  });
}

/**
 * Действующий чек-лист узла.
 *
 * Запрашивается при открытии формы узла: без него человек меняет чек-лист
 * на отделе, ничего не происходит, и причина — унаследованный с региона —
 * ниоткуда не видна.
 */
export function useUnitScorecards(unitId: string | null) {
  return useQuery({
    queryKey: ['units', unitId, 'scorecard'],
    queryFn: () => getUnitScorecards(unitId!),
    enabled: Boolean(unitId),
  });
}

export function useComments(callId: string | null) {
  return useQuery({
    queryKey: queryKeys.callComments(callId ?? ''),
    queryFn: () => listComments(callId!),
    enabled: Boolean(callId),
  });
}

/**
 * Поиск пути узла по его идентификатору.
 *
 * Нужен, чтобы понять, покрывает ли грант узел звонка: гранты несут только
 * `unitId`, а вложенность выражена путями в дереве.
 */
export function useUnitPaths() {
  const units = useUnits();
  const paths = new Map<string, string>();
  for (const unit of flattenTree(units.data)) paths.set(unit.id, unit.path);
  return (unitId: string) => paths.get(unitId);
}

function flattenTree(units: Unit[] | undefined): Unit[] {
  if (!units) return [];
  return units.flatMap((unit) => [unit, ...flattenTree(unit.children)]);
}

/** Каталог шаблонов. Живёт в коде бэкенда и меняется только выкаткой. */
export function useScorecardTemplates(enabled: boolean) {
  return useQuery({
    queryKey: ['scorecard-templates'],
    queryFn: listScorecardTemplates,
    enabled,
    staleTime: 10 * 60_000,
  });
}

// ──────────────────────────────── Теги ─────────────────────────────────

export function useTags(search?: string) {
  return useQuery({
    queryKey: ['tags', search ?? ''],
    queryFn: () => listTags(search),
    staleTime: 60_000,
  });
}

export function useTagRules(enabled: boolean) {
  return useQuery({
    queryKey: ['tag-rules'],
    queryFn: listTagRules,
    enabled,
  });
}

export function useContact(contactId: string | null) {
  return useQuery({
    queryKey: ['contacts', contactId],
    queryFn: () => getContact(contactId!),
    enabled: Boolean(contactId),
  });
}

// ──────────────────────────── Оповещения ───────────────────────────────

/**
 * Оповещения и счётчик непрочитанных.
 *
 * Обновляется само раз в минуту: смысл оповещений в том, чтобы о плохом
 * разговоре узнали в тот же день, а не при следующей перезагрузке страницы.
 */
export function useNotifications(unread?: boolean) {
  return useQuery({
    queryKey: ['notifications', unread ?? false],
    queryFn: () => listNotifications(unread),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useNotificationRules(enabled: boolean) {
  return useQuery({
    queryKey: ['notification-rules'],
    queryFn: listNotificationRules,
    enabled,
  });
}

export function useDeliveries(enabled: boolean) {
  return useQuery({
    queryKey: ['notification-deliveries'],
    queryFn: listDeliveries,
    enabled,
  });
}

/* ------------------------------------------------------------------ */
/* Исходящие интеграции                                                 */
/* ------------------------------------------------------------------ */

/**
 * `enabled` — потому что раздел только для администратора: остальным запрос
 * вернёт 403, и звать его незачем.
 */
export function useIntegrations(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.integrations,
    queryFn: listIntegrations,
    enabled,
  });
}

export function useIntegration(id: string | null) {
  return useQuery({
    queryKey: queryKeys.integration(id ?? 'none'),
    queryFn: () => getIntegration(id as string),
    enabled: Boolean(id),
  });
}

/**
 * Справочники чужой системы. Только для сохранённой интеграции: до неё
 * спрашивать нечем — нужны её адрес и секрет.
 */
export function useIntegrationDictionaries(id: string | null) {
  return useQuery({
    queryKey: queryKeys.integrationDictionaries(id ?? 'none'),
    queryFn: () => getIntegrationDictionaries(id as string),
    enabled: Boolean(id),
    staleTime: 5 * 60_000,
  });
}

/** Соответствия источников и целей чужой системе. */
export function useIntegrationMappings(id: string | null) {
  return useQuery({
    queryKey: queryKeys.integrationMappings(id ?? 'none'),
    queryFn: () => getIntegrationMappings(id as string),
    enabled: Boolean(id),
  });
}

/** Меняется только с релизом бэкенда — держим долго. */
export function useIntegrationKinds(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.integrationKinds,
    queryFn: listIntegrationKinds,
    enabled,
    staleTime: 10 * 60_000,
  });
}

/**
 * Журнал доставок. Обновляется сам: в него смотрят как раз тогда, когда
 * доставка идёт прямо сейчас, и ручное «обновить» здесь лишняя работа.
 */
export function useOutboundDeliveries(filters: DeliveryFilters, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.outboundDeliveries(filters),
    queryFn: () => listOutboundDeliveries(filters),
    enabled,
    refetchInterval: 30_000,
    placeholderData: (previous) => previous,
  });
}

/**
 * Сверка с конверсией. Исходы приезжают синхронизацией раз в час, поэтому
 * частое перечитывание ничего не добавляет.
 */
export function useConversion(filters: ConversionFilters, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.conversion(filters),
    queryFn: () => getConversion(filters),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useCallFields() {
  return useQuery({
    queryKey: ['call-fields'],
    queryFn: listCallFields,
    staleTime: 5 * 60_000,
  });
}

export function useCallFieldTemplates(enabled: boolean) {
  return useQuery({
    queryKey: ['call-field-templates'],
    queryFn: listCallFieldTemplates,
    enabled,
    staleTime: 10 * 60_000,
  });
}

/* ------------------------------------------------------------------ */
/* Коллтрекинг                                                          */
/* ------------------------------------------------------------------ */

/**
 * Пулы со сводкой по номерам и сайтам.
 *
 * Читает любой авторизованный: состав пула объясняет карточку звонка, и
 * прятать его от супервизора незачем.
 */
export function useCtPools() {
  return useQuery({ queryKey: queryKeys.ctPools, queryFn: listPools });
}

/**
 * Занятость номеров. Обновляется сама: `held` и `quarantine` живут минутами, а
 * на экран заходят именно затем, чтобы увидеть, хватает ли номеров сейчас.
 */
export function useCtNumbers(poolId?: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.ctNumbers(poolId),
    queryFn: () => listCtNumbers(poolId),
    enabled,
    refetchInterval: 30_000,
  });
}

/** Что заведено на станции и ещё не в коллтрекинге. */
export function useCtAvailableNumbers(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.ctAvailableNumbers,
    queryFn: listAvailableNumbers,
    enabled,
  });
}

export function useCtSites(enabled = true) {
  return useQuery({ queryKey: queryKeys.ctSites, queryFn: listCtSites, enabled });
}

export function useCtStaticRules(siteId?: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.ctStaticRules(siteId),
    queryFn: () => listStaticRules(siteId),
    enabled,
  });
}

/** Служебная выдача: смотрят её, когда подмена «не работает». */
export function useCtSessions(filters: CtSessionFilters, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.ctSessions(filters),
    queryFn: () => listCtSessions(filters),
    enabled,
    refetchInterval: 30_000,
  });
}

/**
 * Цели звонка.
 *
 * Читаются вместе с разбором: по ним видно, что считается успехом, и это
 * объясняет цифры отчёта не хуже самого отчёта.
 */
export function useCallGoals(status?: CallGoalStatus, enabled = true) {
  return useQuery({
    queryKey: queryKeys.callGoals(status),
    queryFn: () => listCallGoals(status),
    enabled,
  });
}

/** Справочник названий кампаний: меняется загрузкой, не сам по себе. */
export function useAdNames(source: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.adNames(source),
    queryFn: () => listAdNames(source),
    enabled,
    staleTime: 60_000,
  });
}

export function useUnitAddenda(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.unitAddenda,
    queryFn: listUnitAddenda,
    enabled,
  });
}

/** Пустой ответ законный: своих дополнений у узла нет. */
export function useUnitAddendum(unitId: string | null) {
  return useQuery({
    queryKey: queryKeys.unitAddendum(unitId ?? 'none'),
    queryFn: () => getUnitAddendum(unitId as string),
    enabled: Boolean(unitId),
  });
}

/**
 * Чем кормили звонок. Запрашивается только по раскрытию: спрашивают это редко
 * и по поводу, а тянуть промты на каждую открытую карточку незачем.
 */
export function useAnalysisInput(callId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.analysisInput(callId ?? 'none'),
    queryFn: () => getAnalysisInput(callId as string),
    enabled: enabled && Boolean(callId),
    staleTime: 5 * 60_000,
  });
}

/** Качество обработки: сколько звонков мы потеряли сами. */
export function useCtHandlingReport(filters: CtSourcesFilters, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.ctHandlingReport(filters),
    queryFn: () => getCtHandlingReport(filters),
    enabled,
    staleTime: 60_000,
  });
}

/** Отчёт по источникам: обычная аналитика, живёт как остальные агрегаты. */
export function useCtSourcesReport(filters: CtSourcesFilters, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.ctSourcesReport(filters),
    queryFn: () => getCtSourcesReport(filters),
    enabled,
    staleTime: 60_000,
  });
}

/**
 * Отчёт по лендингам и пулам.
 *
 * Состав пулов в нём — на момент запроса, поэтому держим его недолго: иначе
 * «свободно 38» на экране будет старше, чем выглядит.
 */
export function useCtSitesReport(period: { from?: string; to?: string }, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.ctSitesReport(period),
    queryFn: () => getCtSitesReport(period),
    enabled,
    staleTime: 30_000,
  });
}

/* ------------------------------------------------------------------ */
/* Дашборд и отчёты                                                     */
/* ------------------------------------------------------------------ */

/**
 * Агрегаты живут дольше карточек: пересчёт за месяц стоит дорого, а цифра за
 * прошедший период не меняется. Минута — компромисс для сегодняшних суток,
 * которые ещё набираются.
 */
const REPORT_STALE = 60_000;

export function useDashboardMetrics(filters: ReportFilters, compare = false) {
  return useQuery({
    queryKey: queryKeys.dashboard(filters, compare),
    queryFn: () => getDashboardMetrics(filters, compare),
    staleTime: REPORT_STALE,
  });
}

export function useSummary(filters: ReportFilters, groupBy: 'unit' | 'source' | 'none') {
  return useQuery({
    queryKey: queryKeys.summary(filters, groupBy),
    queryFn: () => getSummary(filters, groupBy),
    staleTime: REPORT_STALE,
  });
}

export function useActivityHeatmap(filters: ReportFilters, enabled = true) {
  return useQuery({
    queryKey: queryKeys.heatmap(filters),
    queryFn: () => getActivityHeatmap(filters),
    enabled,
    staleTime: REPORT_STALE,
  });
}

export function useScoreDynamics(
  filters: ReportFilters,
  bucket: 'day' | 'week',
  groupBy: 'unit' | 'none',
  enabled = true
) {
  return useQuery({
    queryKey: queryKeys.scoreDynamics(filters, bucket, groupBy),
    queryFn: () => getScoreDynamics(filters, bucket, groupBy),
    enabled,
    staleTime: REPORT_STALE,
  });
}

export function useTopViolations(filters: ReportFilters, limit = 10, enabled = true) {
  return useQuery({
    queryKey: queryKeys.topViolations(filters, limit),
    queryFn: () => getTopViolations(filters, limit),
    enabled,
    staleTime: REPORT_STALE,
  });
}

export function useViolations(
  filters: ReportFilters,
  groupBy: 'unit' | 'employee',
  enabled = true
) {
  return useQuery({
    queryKey: queryKeys.violations(filters, groupBy),
    queryFn: () => getViolations(filters, groupBy),
    enabled,
    staleTime: REPORT_STALE,
  });
}

export function useRating(
  filters: ReportFilters,
  sortBy: 'score' | 'calls' | 'missed' | 'lost' | 'talk',
  enabled = true
) {
  return useQuery({
    queryKey: queryKeys.rating(filters, sortBy),
    queryFn: () => getRating(filters, sortBy),
    enabled,
    staleTime: REPORT_STALE,
  });
}

/** Разрез по правилам грузится только когда строку раскрыли. */
/** Перехват между отделами: ось «кому звонили», а не «чей звонок». */
export function useHandoff(filters: ReportFilters, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.handoff(filters),
    queryFn: () => getHandoff(filters),
    enabled,
    staleTime: 60_000,
  });
}

export function useEmployeeRules(employeeId: string | null, filters: ReportFilters) {
  return useQuery({
    queryKey: queryKeys.employeeRules(employeeId ?? '', filters),
    queryFn: () => getEmployeeRules(employeeId!, filters),
    enabled: Boolean(employeeId),
    staleTime: REPORT_STALE,
  });
}

/* ------------------------------------------------------------------ */
/* Рабочий список пропущенных                                           */
/* ------------------------------------------------------------------ */

/**
 * Очередь на утро.
 *
 * Живёт короче отчётов: список открывают, чтобы обзвонить, и пока по нему
 * работают, состояния меняются — чужая отметка или перезвон клиента должны
 * доехать сами, иначе двое возьмут одну и ту же строку.
 */
export function useMissedCalls(filters: MissedFilters) {
  return useQuery({
    queryKey: ['missed-calls', filters],
    queryFn: ({ signal }) => listMissedCalls(filters, signal),
    placeholderData: (previous) => previous,
    refetchInterval: 60_000,
    staleTime: 15_000,
  });
}

/** Отметки читают, открыв звонок, — поэтому запрос по требованию. */
export function useMissedActions(callId: string | null) {
  return useQuery({
    queryKey: ['missed-actions', callId ?? ''],
    queryFn: () => listMissedActions(callId!),
    enabled: Boolean(callId),
  });
}

/* ------------------------------------------------------------------ */
/* Глобальный администратор                                            */
/* ------------------------------------------------------------------ */

/**
 * `enabled` — по `isPlatformStaff`, а не по ответу сервера: ветка отвечает
 * 404, и запрос от чужого выглядел бы поломкой экрана, которого он и не
 * должен видеть.
 */
export function usePendingOrganizations(enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'pending-organizations'],
    queryFn: () => listPendingOrganizations(),
    enabled,
    refetchInterval: 60_000,
  });
}

export function useAdminTenants(enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: listAdminTenants,
    enabled,
  });
}

export function useTenantAdmins(tenantId: string | null) {
  return useQuery({
    queryKey: ['admin', 'tenants', tenantId ?? '', 'admins'],
    queryFn: () => listTenantAdmins(tenantId!),
    enabled: Boolean(tenantId),
  });
}

/** Каталог наборов тегов — читаем, только когда открыли окно. */
export function useTagTemplates(enabled: boolean) {
  return useQuery({
    queryKey: ['tag-templates'],
    queryFn: listTagTemplates,
    enabled,
    staleTime: 10 * 60_000,
  });
}

/** Только платформе: `enabled` по признаку, а не по ответу — ветка даёт 404. */
export function useStorageProfiles(enabled: boolean) {
  return useQuery({
    queryKey: ['storage-profiles'],
    queryFn: listStorageProfiles,
    enabled,
  });
}

/** Мои подписки на оповещения. Всегда свои — чужих эта ветка не отдаёт. */
export function useNotificationSettings() {
  return useQuery({
    queryKey: ['notification-settings'],
    queryFn: getNotificationSettings,
  });
}

/* Настройка разбора: промты и словарь. */

export function useAnalysisSettings() {
  return useQuery({ queryKey: ['analysis-settings'], queryFn: getAnalysisSettings });
}

export function useVocabulary() {
  return useQuery({ queryKey: ['vocabulary'], queryFn: listVocabulary });
}
